import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp } from "lucide-react";
import { MonthYearPicker } from '@/components/common/MonthYearPicker';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { getEffectiveTransactionDate } from '@/lib/utils';
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Category } from "@/types/database";

interface Transaction {
  id: string;
  tipo: "entrada" | "saida";
  valor: number;
  data: string;
  data_pagamento?: string;
  foi_paga: boolean;
  ignorar_transacao: boolean;
  categoria_id?: string;
  categoria?: Category;
}

interface MonthlyIncomeReportProps {
  transactions: Transaction[];
  selectedDate?: Date;
  categories?: Category[];
}

export function MonthlyIncomeReport({ transactions, selectedDate, categories = [] }: MonthlyIncomeReportProps) {
  // Buscar contas para calcular datas efetivas
  const { data: accounts = [] } = useQuery({
    queryKey: ['contas'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contas')
        .select('*')
        .eq('ativo', true);
      
      if (error) throw error;
      return data || [];
    }
  });

  // Helper function to find the latest month with income data
  const getLatestMonthWithData = (transactions: Transaction[]) => {
    if (!transactions || transactions.length === 0) {
      return new Date();
    }
    
    const incomes = transactions.filter(t => {
      try {
        return t && t.tipo === 'entrada' && !t.ignorar_transacao && t.data;
      } catch (e) {
        return false;
      }
    });
    
    if (incomes.length === 0) {
      // If no incomes, try to find any transaction with data
      const allValid = transactions.filter(t => t && t.data && !t.ignorar_transacao);
      if (allValid.length > 0) {
        const dates = allValid.map(t => {
          try {
            const date = typeof t.data === 'string' ? new Date(t.data) : new Date(t.data);
            return isNaN(date.getTime()) ? null : date;
          } catch (e) {
            return null;
          }
        }).filter(date => date !== null);
        
        if (dates.length > 0) {
          return new Date(Math.max(...dates.map(d => d.getTime())));
        }
      }
      return new Date();
    }
    
    const dates = incomes.map(t => {
      try {
        const date = typeof t.data === 'string' ? new Date(t.data) : new Date(t.data);
        return isNaN(date.getTime()) ? null : date;
      } catch (e) {
        return null;
      }
    }).filter(date => date !== null);
    
    return dates.length > 0 ? new Date(Math.max(...dates.map(d => d.getTime()))) : new Date();
  };

  const [selectedMonth, setSelectedMonth] = useState(() => {
    if (selectedDate) {
      return selectedDate.getMonth();
    }
    return new Date().getMonth();
  });
  
  const [selectedYear, setSelectedYear] = useState(() => {
    if (selectedDate) {
      return selectedDate.getFullYear();
    }
    return new Date().getFullYear();
  });

  // Update selected month/year when selectedDate prop changes
  useEffect(() => {
    if (selectedDate) {
      setSelectedMonth(selectedDate.getMonth());
      setSelectedYear(selectedDate.getFullYear());
    }
  }, [selectedDate]);

  // Update selected month/year when transactions are loaded (only if no selectedDate is provided)
  useEffect(() => {
    if (!selectedDate && transactions && transactions.length > 0) {
      const latestMonth = getLatestMonthWithData(transactions);
      setSelectedMonth(latestMonth.getMonth());
      setSelectedYear(latestMonth.getFullYear());
    }
  }, [transactions, selectedDate]);

  // Validar selectedYear e selectedMonth para uso em todo o componente
  const validYear = typeof selectedYear === 'number' && selectedYear >= 1900 && selectedYear <= 2100
    ? selectedYear
    : new Date().getFullYear();
  const validMonth = typeof selectedMonth === 'number' && selectedMonth >= 0 && selectedMonth <= 11 
    ? selectedMonth 
    : new Date().getMonth();

  // Filtro por categoria (receitas)
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const incomeCategories = useMemo(() => {
    return (categories || []).filter((c) => c && (c.tipo === 'entrada' || c.tipo === 'ambos'));
  }, [categories]);
  useEffect(() => {
    if (selectedCategory !== 'all') {
      const exists = incomeCategories.some((c) => c.nome === selectedCategory);
      if (!exists) setSelectedCategory('all');
    }
  }, [incomeCategories, selectedCategory]);

  const monthlyData = useMemo(() => {
    const currentMonthDate = new Date(validYear, validMonth);
    
    // Verificar se a data criada é válida
    if (isNaN(currentMonthDate.getTime())) {
      return { dailyIncomes: [], monthTotal: 0 };
    }
    
    const monthStart = startOfMonth(currentMonthDate);
    const monthEnd = endOfMonth(currentMonthDate);

    // Base de dias para o mês selecionado
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
    const baseDaily = days.map(day => ({
      day,
      dayNumber: day.getDate(),
      label: format(day, 'dd', { locale: ptBR }),
      fullLabel: format(day, 'dd MMMM', { locale: ptBR })
    }));

    // Agregar valores por dia usando extração UTC (evita problemas de fuso)
    const dayNumberToTotal = new Map<number, number>();

    for (const t of transactions) {
      if (!t || t.tipo !== 'entrada' || t.ignorar_transacao) {
        continue;
      }

      // Filtrar por categoria selecionada
      if (selectedCategory !== 'all') {
        const transactionCategory = t.categoria?.nome || t.categoria_id;
        if (transactionCategory !== selectedCategory) {
          continue;
        }
      }
      
      // Usar data efetiva (considerando cartão de crédito)
      // @ts-expect-error - Transaction type compatible
      const txDate = getEffectiveTransactionDate(t, accounts || []);
      
      if (!txDate || !(txDate instanceof Date) || isNaN(txDate.getTime())) {
        continue;
      }

      const txYear = txDate.getUTCFullYear();
      const txMonth = txDate.getUTCMonth();
      const txDay = txDate.getUTCDate();
      
      if (txYear !== validYear || txMonth !== validMonth) {
        continue;
      }

      const prev = dayNumberToTotal.get(txDay) || 0;
      dayNumberToTotal.set(txDay, prev + Number(t.valor || 0));
    }

    const dailyIncomes = baseDaily.map(d => {
      const total = dayNumberToTotal.get(d.dayNumber) || 0;

      return {
        day: d.label,
        fullDay: d.fullLabel,
        value: total,
        dayNumber: d.dayNumber
      };
    });

    const monthTotal = dailyIncomes.reduce((sum, day) => sum + day.value, 0);

    return { dailyIncomes, monthTotal };
  }, [transactions, validMonth, validYear, accounts, selectedCategory]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <div className="space-y-6">
      {/* Total Summary Card */}
      <Card className="bg-card border-border">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base font-medium text-muted-foreground">
            Total Receitas por Mês
            {selectedCategory !== 'all' && (
              <span className="text-xs font-normal text-foreground block">
                Categoria: {selectedCategory}
              </span>
            )}
          </CardTitle>
          <div className="p-2 rounded-md bg-primary text-primary-foreground">
            <TrendingUp className="h-4 w-4" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-base font-bold text-primary">
            {formatCurrency(monthlyData.monthTotal)}
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {format(new Date(validYear, validMonth), 'MMMM yyyy', { locale: ptBR })}
          </p>
        </CardContent>
      </Card>

      {/* Enhanced Month/Year Navigation */}
      <div className="grid grid-cols-1 sm:grid-cols-3 items-center gap-4">
        <div className="hidden sm:block" />
        <div className="flex justify-center">
          <MonthYearPicker
            selectedDate={new Date(validYear, validMonth, 1)}
            onDateChange={(date) => {
              setSelectedMonth(date.getMonth());
              setSelectedYear(date.getFullYear());
            }}
          />
        </div>
        {/* Filtro de Categoria */}
        <div className="w-full sm:w-auto flex sm:justify-end">
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-full sm:w-40">
              <SelectValue placeholder="Filtrar por categoria" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as categorias</SelectItem>
              {incomeCategories.map((category) => (
                <SelectItem key={category.id} value={category.nome}>
                  {category.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Receitas do mês</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthlyData.dailyIncomes}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="day" 
                    className="text-xs fill-muted-foreground"
                    tick={{ fontSize: 10 }}
                  />
                  <YAxis 
                    className="text-xs fill-muted-foreground"
                    tick={{ fontSize: 10 }}
                    tickFormatter={(value) => 
                      new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      }).format(value)
                    }
                  />
                  <Tooltip 
                    formatter={(value: number) => [formatCurrency(value), 'Receitas']}
                    labelFormatter={(label) => `Dia ${label}`}
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />

                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2}
                    dot={{ fill: 'hsl(var(--primary))', strokeWidth: 2, r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Daily List */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Detalhes Mensais</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-2">
              {monthlyData.dailyIncomes
                .filter(day => day.value > 0)
                .map((day, index) => (
                <div key={index} className="flex justify-between items-center py-2 border-b border-border last:border-b-0">
                  <span className="text-sm font-medium">
                    {day.dayNumber} {format(new Date(validYear, validMonth), 'MMMM', { locale: ptBR })}
                  </span>
                  <span className="text-sm font-mono">
                    {formatCurrency(day.value)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
